import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../Header';
import Footer from '../Footer';
import AnnouncementBanner from '../AnnouncementBanner';
import FloatingWhatsApp from '../FloatingWhatsApp';
import MobileBottomNav from '../MobileBottomNav';
import SEOSchema from '../SEOSchema';
import RelatedResources from '../RelatedResources';
import { ChevronRight, Calculator, ArrowRight, Info, ChevronDown, ShieldAlert, Stethoscope } from 'lucide-react';
import { CALCULATOR_ABOUT, CALCULATOR_DISCLAIMER } from '../../data/calculatorAbout';
import { CALCULATOR_GUIDANCE } from '../../data/calculatorGuidance';

/**
 * Premium shell wrapper for every calculator page.
 * Renders: SEO head, header, breadcrumb, hero (title + subtitle), 2-col content (inputs + result),
 * how-it-works section, CTA, and footer.
 *
 * Props:
 *   slug: route slug (used for canonical)
 *   title: SEO + on-page title
 *   subtitle: short subtitle below hero
 *   metaDescription: SEO description
 *   accentGradient: tailwind gradient classes for hero badge/CTA
 *   accentColor: tailwind text-color class for highlights (e.g. 'text-emerald-400')
 *   icon: lucide React component for the hero icon
 *   formulaText: optional explanation block (string or JSX)
 *   inputs: ReactNode for the inputs form column
 *   result: ReactNode for the result card column
 */
const CalculatorLayout = ({
  slug,
  title,
  subtitle,
  metaDescription,
  accentGradient = 'from-blue-500 to-cyan-500',
  accentColor = 'text-blue-400',
  badgeBorder = 'border-blue-500/30',
  badgeBg = 'bg-blue-500/10',
  icon: Icon = Calculator,
  formulaText,
  inputs,
  result,
  ctaTitle = 'Need a personalized plan?',
  ctaSubtitle = 'Your calculator result is a starting point. Get a doctor-guided plan tailored to your medical history.'
}) => {
  const about = CALCULATOR_ABOUT[slug];
  const guidance = CALCULATOR_GUIDANCE[slug];
  const [aboutOpen, setAboutOpen] = useState(true);

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>{title} | TCUBE FITNESS™ Calculators</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://tcubefitness.com/calculators/${slug}`} />
      </Helmet>

      <SEOSchema
        type="calculator"
        name={title}
        description={metaDescription}
        url={`/calculators/${slug}`}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Calculators', path: '/calculators' },
          { name: title, path: `/calculators/${slug}` },
        ]}
      />

      <AnnouncementBanner />
      <Header />

      <main id="main-content" tabIndex={-1} className="pt-32 sm:pt-36 pb-24 lg:pb-16">
        <div className="container mx-auto px-4 sm:px-6">

          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6 text-xs sm:text-sm">
            <ol className="flex items-center gap-1.5 text-slate-500 flex-wrap">
              <li><Link to="/" className="hover:text-white">Home</Link></li>
              <li><ChevronRight className="w-3.5 h-3.5" aria-hidden="true" /></li>
              <li><Link to="/calculators" className="hover:text-white">Calculators</Link></li>
              <li><ChevronRight className="w-3.5 h-3.5" aria-hidden="true" /></li>
              <li className="text-slate-300">{title}</li>
            </ol>
          </nav>

          {/* Hero */}
          <section className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <span className={`inline-flex items-center gap-2 px-4 py-2 ${badgeBg} border ${badgeBorder} rounded-full ${accentColor} text-sm font-medium mb-5`}>
              <Icon className="w-4 h-4" aria-hidden="true" />
              {title}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              <span className={`bg-gradient-to-r ${accentGradient} bg-clip-text text-transparent`}>{title}</span>
            </h1>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              {subtitle}
            </p>
          </section>

          {/* Inputs + Result */}
          <section className="grid lg:grid-cols-2 gap-6 lg:gap-8 max-w-6xl mx-auto">
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8" data-testid="calculator-inputs">
              {inputs}
            </div>
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8" data-testid="calculator-result">
              {result}
            </div>
          </section>

          {/* Result guidance: plain-language meaning + limitation + booking prompt */}
          {guidance && (
            <section className="max-w-4xl mx-auto mt-10 sm:mt-12" data-testid="calculator-guidance">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
                  <h2 className="text-base sm:text-lg font-bold text-white mb-2 flex items-center gap-2">
                    <Info className={`w-4 h-4 ${accentColor}`} aria-hidden="true" /> What your result means
                  </h2>
                  <p className="text-slate-300 text-sm sm:text-base leading-relaxed">{guidance.whatItMeans}</p>
                </div>
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6">
                  <h2 className="text-base sm:text-lg font-bold text-amber-200 mb-2 flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4" aria-hidden="true" /> Good to know
                  </h2>
                  <p className="text-slate-300 text-sm sm:text-base leading-relaxed">{guidance.limitation}</p>
                </div>
              </div>
              <p className="text-center text-slate-400 text-sm mt-4">
                A number is a starting point, not a plan.{' '}
                <Link to={`/book-consultation?from=calc-guidance-${slug}`} className={`${accentColor} font-semibold hover:underline underline-offset-4`} data-testid="calculator-guidance-cta">
                  Discuss your result with a doctor
                </Link>
              </p>
            </section>
          )}

          {/* About this calculator (auto-mapped) */}
          {about && (
            <section className="max-w-4xl mx-auto mt-10 sm:mt-12" data-testid="about-calculator-section">
              <div className={`bg-gradient-to-br from-slate-900/80 to-slate-900/40 border ${badgeBorder} rounded-2xl overflow-hidden`}>
                <button
                  type="button"
                  onClick={() => setAboutOpen((v) => !v)}
                  aria-expanded={aboutOpen}
                  data-testid="about-calculator-toggle"
                  className="w-full flex items-center justify-between gap-3 px-5 sm:px-6 py-4 text-left hover:bg-slate-800/40 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl ${badgeBg} border ${badgeBorder} flex items-center justify-center ${accentColor} flex-shrink-0`}>
                      <Stethoscope className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                    </div>
                    <h2 className="text-base sm:text-lg lg:text-xl font-bold text-white">{about.title}</h2>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${aboutOpen ? `rotate-180 ${accentColor}` : 'text-slate-500'}`}
                    aria-hidden="true"
                  />
                </button>
                <div
                  className={`grid transition-all duration-300 ease-in-out ${aboutOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                  data-testid="about-calculator-body"
                >
                  <div className="overflow-hidden">
                    <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-1">
                      <div className={`border-t border-slate-800 pt-4 flex gap-3 sm:gap-4`}>
                        <div className="flex-shrink-0">
                          <div className={`w-8 h-8 rounded-full bg-slate-800/80 border border-slate-700 flex items-center justify-center ${accentColor}`}>
                            <Info className="w-4 h-4" aria-hidden="true" />
                          </div>
                        </div>
                        <p className="text-slate-300 text-sm sm:text-base leading-relaxed whitespace-pre-line">
                          {about.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Formula / how-it-works */}
          {formulaText && (
            <section className="max-w-4xl mx-auto mt-12 bg-slate-900/40 border border-slate-800 rounded-2xl p-6 sm:p-8" data-testid="calculator-formula">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">How this is calculated</h2>
              <div className="text-slate-400 text-sm sm:text-base leading-relaxed space-y-2">
                {formulaText}
              </div>
            </section>
          )}

          {/* Related internal links (SEO) */}
          <div className="max-w-4xl mx-auto mt-12">
            <RelatedResources slug={slug} />
          </div>

          {/* CTA */}
          <section className={`max-w-4xl mx-auto mt-12 bg-gradient-to-br ${accentGradient} bg-opacity-20 border ${badgeBorder} rounded-3xl p-8 sm:p-10 text-center relative overflow-hidden`}>
            <div className="absolute inset-0 bg-slate-950/70" aria-hidden="true"></div>
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">{ctaTitle}</h2>
              <p className="text-slate-300 mb-6 max-w-xl mx-auto text-sm sm:text-base">{ctaSubtitle}</p>
              <Link
                to={`/book-consultation?from=calc-${slug}`}
                className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-100 px-7 py-3.5 rounded-full font-bold text-sm sm:text-base shadow-lg transition-all"
                data-testid="calculator-cta-book"
              >
                Book Free Consultation
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </section>

          {/* Disclaimer (always visible at the bottom) */}
          <section
            className="max-w-4xl mx-auto mt-12 bg-amber-500/5 border border-amber-500/20 rounded-2xl p-5 sm:p-6"
            role="note"
            aria-labelledby="calc-disclaimer-heading"
            data-testid="calculator-disclaimer"
          >
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-300 flex-shrink-0">
                <ShieldAlert className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
              </div>
              <div>
                <h2 id="calc-disclaimer-heading" className="text-sm sm:text-base font-bold text-amber-200 mb-2 uppercase tracking-wider">Disclaimer</h2>
                <div className="text-slate-300 text-xs sm:text-sm leading-relaxed whitespace-pre-line">
                  {CALCULATOR_DISCLAIMER}
                </div>
              </div>
            </div>
          </section>

        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />
      <MobileBottomNav />
    </div>
  );
};

export default CalculatorLayout;
